import { createSelectorFunctions } from 'auto-zustand-selectors-hook'
import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'

import { PaytrieAPI } from '@paytrie/client'

import {
  NetworkValue,
  Networks,
  PaymentType,
  PaymentTypes,
  StableCoinValue,
  StableCoins,
  getSupportedCurrencies,
  MAX_24_999_SELF_SERVE_AMOUNT,
  isFiat,
  getNetwork,
  getStableCoinType,
} from '@/utils/currencies'

export const TransactionType = {
  BUY: 'BUY',
  SELL: 'SELL',
} as const

type ValueOf<T> = T[keyof T]

export type TransactionValue = ValueOf<typeof TransactionType>

export type BuySellState = {
  transactionType: TransactionValue
  fiatAmount: number
  stableCoinAmount: number
  referralAdjustedFiatAmount: number
  referralAdjustedCoinAmount: number
  requestType: PaymentType
  network: NetworkValue
  stableCoin: StableCoinValue
  loading: boolean
  latestV2Quote: PaytrieAPI.Quote | null
  dialogPage1: boolean
  inputsDisabled: boolean
}

// TransactionType logic:
// First check if query params from sessionStorage set leftSidelabel/rightSidelabel
// If not set, then check sessionStorage for last transactionType
// otherwise default to BUY

const urlParams = new URLSearchParams(window.location.search)
const LSLParams = urlParams.get('leftSideLabel')
const RSLParams = urlParams.get('rightSideLabel')

const sessionTransactionType = sessionStorage.getItem('transactionType')
const leftSideLabel = LSLParams || sessionStorage.getItem('leftSideLabel') || ''
const rightSideLabel =
  RSLParams || sessionStorage.getItem('rightSideLabel') || ''

//const isLeftFiat = leftSideLabel && isFiat(leftSideLabel);
//const isRightFiat = rightSideLabel && isFiat(rightSideLabel);
let isLeftFiat, isRightFiat

if (leftSideLabel.length > 0) {
  isLeftFiat = leftSideLabel.length > 0 && isFiat(leftSideLabel)
  isRightFiat = !isLeftFiat
} else if (rightSideLabel.length > 0) {
  isRightFiat = rightSideLabel.length > 0 && isFiat(rightSideLabel)
  isLeftFiat = !isRightFiat
}

const stableCoinVal = isLeftFiat
  ? getStableCoinType(rightSideLabel.substring(0, rightSideLabel.indexOf('-')))
  : isRightFiat
    ? getStableCoinType(leftSideLabel.substring(0, leftSideLabel.indexOf('-')))
    : StableCoins.USDC

const networkVal = isLeftFiat
  ? getNetwork(
      rightSideLabel.substring(
        rightSideLabel.indexOf('-') + 1,
        rightSideLabel.length
      )
    )
  : isRightFiat
    ? getNetwork(
        leftSideLabel.substring(
          leftSideLabel.indexOf('-') + 1,
          leftSideLabel.length
        )
      )
    : Networks.ethereum
//console.log("NETWORKVAL:", networkVal);
//console.log("STABLECOINVAL:", stableCoinVal);

// If we have query params,  enforce initial tType from that first
// Otherwise, enforce from sessionTransactionType
let initialTransactionType: TransactionValue = TransactionType.BUY
if (LSLParams || RSLParams) {
  if (isRightFiat) {
    initialTransactionType = TransactionType.SELL
  }
} else {
  //initialTransactionType = sessionTransactionType == TransactionType.SELL ? TransactionType.SELL : TransactionType.BUY;
  if (sessionTransactionType == TransactionType.SELL.toString()) {
    initialTransactionType = TransactionType.SELL
  }
}

const parseAmountParam = (value: string | null) => {
  const parsed = Number(value)
  return value && Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

const LSVParam = parseAmountParam(urlParams.get('leftSideValue'))
const RSVParam = parseAmountParam(urlParams.get('rightSideValue'))
const isInitialBuy = initialTransactionType === TransactionType.BUY
const seededStableCoinAmount = isInitialBuy ? RSVParam : LSVParam
const seededFiatAmount =
  seededStableCoinAmount === null ? (isInitialBuy ? LSVParam : RSVParam) : null

export const getInitialQuoteAnchorSide = (): 'left' | 'right' => {
  if (seededStableCoinAmount !== null) {
    return isInitialBuy ? 'right' : 'left'
  }
  if (seededFiatAmount !== null) {
    return isInitialBuy ? 'left' : 'right'
  }
  return 'left'
}

// Check for inputsDisabled parameter
const inputsDisabledParam = urlParams.get('inputsDisabled')
const isInputsDisabled = inputsDisabledParam === 'true'

// Check for autoConnect parameter (MiniPay integration)
// When autoConnect=true, force Celo network and USDC stablecoin
// Check URL first, then sessionStorage (persists after email auth redirect)
const autoConnectParam =
  urlParams.get('autoConnect') || sessionStorage.getItem('autoConnect')
const isAutoConnect = autoConnectParam === 'true'

if (isAutoConnect) {
  sessionStorage.setItem('lockNetwork', 'true')
}

const initialState: BuySellState = {
  transactionType: initialTransactionType,
  loading: false,
  fiatAmount: seededFiatAmount ?? 1000,
  stableCoinAmount: seededStableCoinAmount ?? (isRightFiat ? 1000 : 0),
  referralAdjustedFiatAmount: 0,
  referralAdjustedCoinAmount: 0,
  network: isAutoConnect ? Networks.celo : networkVal,
  requestType: PaymentTypes.REQUEST,
  stableCoin: isAutoConnect ? StableCoins.USDC : stableCoinVal,
  latestV2Quote: null,
  dialogPage1: true,
  inputsDisabled: isInputsDisabled,
}

type BuySellActions = {
  reset: () => void
  setTransactionType: (transactionType: TransactionValue) => void
  setNetwork: (network: NetworkValue, enableCadcSolana: boolean) => void
  setFiatAmount: (value: number) => void
  setRequestType: (option: PaymentType) => void
  setStableCoinAmount: (value: number) => void
  setStableCoin: (currency: StableCoinValue) => void
  setLoading: (currency: boolean) => void
  setTransactionTypeV2: (transactionType: TransactionValue) => void
  setNetworkV2: (network: NetworkValue) => void
  setFiatAmountV2: (value: number) => void
  setStableCoinAmountV2: (value: number) => void
  setStableCoinV2: (currency: StableCoinValue) => void
  setQuotesV2: (quote: PaytrieAPI.Quote | null) => void
  setReferralAdjustedFiatAmount: (value: number) => void
  setReferralAdjustedCoinAmount: (value: number) => void
  setDialogPage1: (value: boolean) => void
  setInputsDisabled: (value: boolean) => void
}

export const createBuySellStore = create<BuySellState & BuySellActions>()(
  immer<BuySellState & BuySellActions>((set) => ({
    ...initialState,
    reset: () => {
      set(initialState)
      sessionStorage.clear()
    },
    setTransactionType: (transactionType: TransactionValue) =>
      set((state) => {
        sessionStorage.setItem('transactionType', transactionType)
        state.transactionType = transactionType
      }),
    setNetwork: (network: NetworkValue, enableCadcSolana: boolean) =>
      set((state) => {
        state.network = network
        const currencies = getSupportedCurrencies(enableCadcSolana)
        if (!currencies[network].includes(state.stableCoin)) {
          state.stableCoin = currencies[network][0]
        }
      }),
    setRequestType: (option: PaymentType) =>
      set((state) => {
        state.requestType = option
      }),
    setFiatAmount: (value: number) =>
      set((state) => {
        state.fiatAmount = value
      }),
    setStableCoinAmount: (value: number) =>
      set((state) => {
        state.stableCoinAmount = value
      }),
    setReferralAdjustedFiatAmount: (value: number) =>
      set((state) => {
        state.referralAdjustedFiatAmount = value
      }),
    setReferralAdjustedCoinAmount: (value: number) =>
      set((state) => {
        state.referralAdjustedCoinAmount = value
      }),
    setDialogPage1: (value: boolean) =>
      set((state) => {
        state.dialogPage1 = value
      }),
    setInputsDisabled: (value: boolean) =>
      set((state) => {
        state.inputsDisabled = value
      }),
    setStableCoin: (stableCoin: StableCoinValue) =>
      set((state) => {
        state.stableCoin = stableCoin
      }),
    setLoading: (loading: boolean) =>
      set((state) => {
        state.loading = loading
      }),
    setTransactionTypeV2: (transactionType: TransactionValue) =>
      set((state) => {
        sessionStorage.setItem('transactionType', transactionType)
        if (state.transactionType === transactionType) {
          return
        }
        state.transactionType = transactionType
        if (transactionType === TransactionType.SELL) {
          state.stableCoinAmount = state.fiatAmount
        } else {
          state.fiatAmount = state.stableCoinAmount
        }
        state.latestV2Quote = null
      }),
    setNetworkV2: (network: NetworkValue) =>
      set((state) => {
        state.network = network
        const currencies = getSupportedCurrencies(false)
        if (!currencies[network].includes(state.stableCoin)) {
          state.stableCoin = currencies[network][0]
        }
        state.latestV2Quote = null
      }),
    setFiatAmountV2: (value: number) =>
      set((state) => {
        state.fiatAmount = value
      }),
    setStableCoinAmountV2: (value: number) =>
      set((state) => {
        state.stableCoinAmount = value
      }),
    setStableCoinV2: (stableCoin: StableCoinValue) =>
      set((state) => {
        state.stableCoin = stableCoin
        state.latestV2Quote = null
      }),
    setQuotesV2: (quote: PaytrieAPI.Quote | null) =>
      set((state) => {
        state.latestV2Quote = quote
        if (!quote) return

        const leftIsFiat = isFiat(quote.leftSideLabel)
        if (leftIsFiat) {
          state.fiatAmount = quote.leftSideValue
          state.stableCoinAmount = quote.rightSideValue
        } else {
          state.stableCoinAmount = quote.leftSideValue
          state.fiatAmount = quote.rightSideValue
        }
      }),
  }))
)

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
export const useBuySellStore = createSelectorFunctions(createBuySellStore)
